import * as THREE from "three";
import GIF from "gif.js";
import gifWorkerUrl from "url:gif.js/dist/gif.worker.js";
// ffmpeg assets are copied verbatim into dist/ffmpeg/ by
// parcel-reporter-static-files-copy. We resolve them relative to the document
// base URI rather than hardcoding /ffmpeg/... so the app works when deployed
// under a sub-path. The UMD core in particular MUST NOT go through Parcel —
// it loads via importScripts() and assigns self.createFFmpegCore as a side
// effect, which Parcel's tree-shaker would eliminate.
const ffmpegWorkerUrl = new URL("ffmpeg/ffmpeg-worker.js", document.baseURI).toString();
const ffmpegCoreUrl   = new URL("ffmpeg/ffmpeg-core.js",   document.baseURI).toString();
const ffmpegWasmUrl   = new URL("ffmpeg/ffmpeg-core.wasm", document.baseURI).toString();
import "toolcool-range-slider";
import "toolcool-range-slider/dist/plugins/tcrs-moving-tooltip.min.js";
import {
  buildCamera,
  buildControls,
  buildRenderer,
  buildScene,
  buildLight,
  buildGrid,
} from "./scene-utils";
import {
  loadModels,
  changeGumColor,
  toggleVisibility,
  play,
  toggleModelVisibility,
  setModelVisibility,
  changeActiveIndexByValue,
  handleDiffView,
  getActiveAlignerNumbers,
  getMaxAlignerNumber,
  selectTooth,
  deselectTooth,
} from "./model-manager";
import { getToothMapping, findIPRLines, testFDIMapping, updateIPRVisibility, toggleIPR, initializeIPRVisualization, cleanupIPRVisualization, updateLabelPosition, positionMobileIPRLabelFromLineEnd, isMobilePortrait } from "./ipr-visualization";
import TWEEN from "@tweenjs/tween.js";
import axios from "axios";
import canvasManager from "./canvas-manager";

import "normalize.css/normalize.css";

import "@melloware/coloris/dist/coloris.css";
import Coloris from "@melloware/coloris";

import dropStep from "../../../static/assets/dragStep.png";
import { sharedData, VISIBILITY_MODES } from "./model-state";
import { isPortrait } from "./scene-utils";
import MicroModal from "micromodal";
import {
  initMovementPanel,
  toggleMovementPanel,
} from "./movement-table";
MicroModal.init();

const DESKTOP_CAMERA_ZOOM = 1.21;

Coloris.init();
Coloris({
  defaultColor: "#c58c8c",
  alpha: false,
  onChange: (color) => {
    changeGumColor(null, color);
  },
  theme: "polaroid",
});

if (process.env.NAME === "smilealigners") {
  document.getElementById("menu-items").style.backgroundColor = "#f0e0c9";
}

document.body.classList.add(process.env.NAME);

const initializeCanvas = async (canvasId, config = {}) => {
  const canvas = document.getElementById(canvasId);

  const screenDimensions = {
    width: canvas.width,
    height: canvas.height,
  };

  const scene = buildScene();

  const renderer = buildRenderer(
    {
      width: screenDimensions.width,
      height: screenDimensions.height,
    },
    canvas
  );

  const camera = buildCamera({
    width: screenDimensions.width,
    height: screenDimensions.height,
  });

  const controls = buildControls(camera, renderer);

  // Listen to camera changes to update IPR visibility and label positions
  controls.addEventListener('change', () => {
    updateIPRVisibility(canvasId);
  });

  // Also update on window resize to reposition labels
  window.addEventListener('resize', () => {
    updateIPRVisibility(canvasId);
  });

  const { overlayCamera, overlayScene, gridMaterial, centerMaterial } =
    buildGrid({
      width: screenDimensions.width,
      height: screenDimensions.height,
    });

  canvasManager.add(
    canvas.id,
    canvas,
    scene,
    camera,
    renderer,
    controls,
    overlayScene,
    overlayCamera
  );

  await loadModels(canvasId, scene);

  const light = buildLight(scene, renderer);

  window.addEventListener("resize", () =>
    resizeCanvas(
      canvas,
      camera,
      renderer,
      overlayCamera,
      gridMaterial,
      centerMaterial
    )
  );
  resizeCanvas(
    canvas,
    camera,
    renderer,
    overlayCamera,
    gridMaterial,
    centerMaterial
  );

  render(
    renderer,
    camera,
    controls,
    scene,
    light,
    overlayCamera,
    overlayScene,
    canvasId
  );

  window.dispatchEvent(new Event("resize"));
};

// Cleanup handle for the bidirectional camera sync between main and diff
// canvases. Set when diff view is enabled (zenyum only); called and cleared
// when diff view is closed so listeners don't accumulate across toggles.
let cleanupDiffCameraSync = null;

// Mirror camera position/rotation/zoom + controls target between the main and
// diff canvases. The re-entry guard prevents the listener we trigger on the
// other canvas from bouncing back and re-syncing the source mid-update.
const setupDiffCameraSync = () => {
  if (process.env.NAME !== "zenyum") return null;
  const main = canvasManager.get("canvas");
  const diff = canvasManager.get("diff-canvas");
  if (!main || !diff) return null;

  let isSyncing = false;
  const makeHandler = (sourceId, targetId) => () => {
    if (isSyncing) return;
    const source = canvasManager.get(sourceId);
    const target = canvasManager.get(targetId);
    if (!source || !target) return;
    isSyncing = true;
    target.camera.position.copy(source.camera.position);
    target.camera.quaternion.copy(source.camera.quaternion);
    target.camera.zoom = source.camera.zoom;
    target.camera.updateProjectionMatrix();
    target.controls.target.copy(source.controls.target);
    target.controls.update();
    isSyncing = false;
  };

  const mainHandler = makeHandler("canvas", "diff-canvas");
  const diffHandler = makeHandler("diff-canvas", "canvas");
  main.controls.addEventListener("change", mainHandler);
  diff.controls.addEventListener("change", diffHandler);

  return () => {
    main.controls.removeEventListener("change", mainHandler);
    diff.controls.removeEventListener("change", diffHandler);
  };
};

const toggleDiffView = async () => {
  const existing = document.getElementById("diff-canvas");
  if (existing) {
    if (cleanupDiffCameraSync) {
      cleanupDiffCameraSync();
      cleanupDiffCameraSync = null;
    }
    // Clean up IPR visualization before removing canvas
    cleanupIPRVisualization(existing.id);
    canvasManager.remove(existing.id);
    existing.remove();
    window.dispatchEvent(new Event("resize"));
    return;
  }
  const newCanvas = document.createElement("canvas");
  newCanvas.id = "diff-canvas";
  document.querySelector(".renderer-container").prepend(newCanvas);
  await initializeCanvas(newCanvas.id);

  const mainCanvas = canvasManager.get("canvas");
  // Sync maxil/mand state so "disable maxil/mand" turns off for both canvases (otherwise diff has "" and toggles to maxil)
  const syncedMaxilMand = mainCanvas.activeMaxilOrMandView || "";
  canvasManager.update("diff-canvas", {
    hiddenModels: { ...mainCanvas.hiddenModels },
    visibilityMode: mainCanvas.visibilityMode,
    activeMaxilOrMandView: syncedMaxilMand,
  });
  toggleVisibility("diff-canvas", mainCanvas.visibilityMode);

  // Force diff canvas to show only aligner 00 (onModelLoad already did this, but
  // ensure it after syncing visibility so no other aligners stay visible).
  changeActiveIndexByValue("diff-canvas", 0);

  // Restore global upperActiveNum/lowerActiveNum from slider (onModelLoad for diff set them to "00").
  const playerSlider = document.getElementById("player-slider");
  if (playerSlider != null && playerSlider.value != null) {
    changeActiveIndexByValue("canvas", Number(playerSlider.value));
  }

  changeCameraAngle(mainCanvas.camera, "front");

  if (!isPortrait) {
    mainCanvas.camera.zoom = DESKTOP_CAMERA_ZOOM;
  }
  mainCanvas.camera.updateProjectionMatrix();

  handleDiffView();

  cleanupDiffCameraSync = setupDiffCameraSync();
};

/**
 * Set maxil view state (force on or off, no toggle)
 * @param {string} canvasId - Canvas ID
 * @param {boolean} enable - Whether to enable maxil view
 * @param {boolean} forceCameraUpdate - Force camera update even if state is already correct
 */
const setMaxilView = (canvasId, enable, forceCameraUpdate = false) => {
  let { camera, activeMaxilOrMandView, visibilityMode } =
    canvasManager.get(canvasId);

  const shouldBeMaxil = enable;
  const isCurrentlyMaxil = activeMaxilOrMandView === "maxil";
  
  // Update state if needed, or if forcing camera update
  if (shouldBeMaxil !== isCurrentlyMaxil || forceCameraUpdate) {
    if (shouldBeMaxil !== isCurrentlyMaxil) {
      activeMaxilOrMandView = shouldBeMaxil ? "maxil" : "";
      canvasManager.update(canvasId, { activeMaxilOrMandView });
    }
    
    if (shouldBeMaxil) {
      new TWEEN.Tween(camera.position)
        .to(!isPortrait ? { x: 0, y: -90, z: 35 } : { x: 90, y: 0, z: 35 }, 500)
        .easing(TWEEN.Easing.Cubic.InOut)
        .start();
      if (visibilityMode != VISIBILITY_MODES.UPPER) {
        toggleVisibility(canvasId, VISIBILITY_MODES.UPPER);
      }
    } else {
      new TWEEN.Tween(camera.position)
        .to({ x: 0, y: 0, z: 112 }, 500)
        .easing(TWEEN.Easing.Cubic.InOut)
        .start();
      if (visibilityMode != VISIBILITY_MODES.UPPER_LOWER) {
        toggleVisibility(canvasId, VISIBILITY_MODES.UPPER_LOWER);
      }
    }
  }
};

/**
 * Set mand view state (force on or off, no toggle)
 * @param {string} canvasId - Canvas ID
 * @param {boolean} enable - Whether to enable mand view
 * @param {boolean} forceCameraUpdate - Force camera update even if state is already correct
 */
const setMandView = (canvasId, enable, forceCameraUpdate = false) => {
  let { camera, activeMaxilOrMandView, visibilityMode } =
    canvasManager.get(canvasId);

  const shouldBeMand = enable;
  const isCurrentlyMand = activeMaxilOrMandView === "mand";
  
  // Update state if needed, or if forcing camera update
  if (shouldBeMand !== isCurrentlyMand || forceCameraUpdate) {
    if (shouldBeMand !== isCurrentlyMand) {
      activeMaxilOrMandView = shouldBeMand ? "mand" : "";
      canvasManager.update(canvasId, { activeMaxilOrMandView });
    }
    
    if (shouldBeMand) {
      new TWEEN.Tween(camera.position)
        .to(!isPortrait ? { x: 0, y: 90, z: 35 } : { x: -90, y: 0, z: 35 }, 1000)
        .easing(TWEEN.Easing.Cubic.Out)
        .start();
      if (visibilityMode != VISIBILITY_MODES.LOWER) {
        toggleVisibility(canvasId, VISIBILITY_MODES.LOWER);
      }
    } else {
      new TWEEN.Tween(camera.position)
        .to({ x: 0, y: 0, z: 112 }, 1000)
        .easing(TWEEN.Easing.Cubic.Out)
        .start();
      if (visibilityMode != VISIBILITY_MODES.UPPER_LOWER) {
        toggleVisibility(canvasId, VISIBILITY_MODES.UPPER_LOWER);
      }
    }
  }
};

const toggleMaxilView = (canvasId) => {
  let { camera, activeMaxilOrMandView, visibilityMode } =
    canvasManager.get(canvasId);

  if (activeMaxilOrMandView != "maxil") {
    activeMaxilOrMandView = "maxil";
  } else {
    activeMaxilOrMandView = "";
  }
  canvasManager.update(canvasId, { activeMaxilOrMandView });

  if (activeMaxilOrMandView === "maxil") {
    new TWEEN.Tween(camera.position)
      .to(!isPortrait ? { x: 0, y: -90, z: 35 } : { x: 90, y: 0, z: 35 }, 500)
      .easing(TWEEN.Easing.Cubic.InOut)
      .start();
  } else {
    new TWEEN.Tween(camera.position)
      .to({ x: 0, y: 0, z: 112 }, 500)
      .easing(TWEEN.Easing.Cubic.InOut)
      .start();
  }
  // When turning OFF maxil, always set UPPER_LOWER and sync toolbar (don't use visibilityMode:
  // deactivateUpperLowerIfActive may have already set it to UPPER_LOWER before we run).
  if (activeMaxilOrMandView === "") {
    toggleVisibility(canvasId, VISIBILITY_MODES.UPPER_LOWER);
    document
      .querySelectorAll(".toolbar-item.upper_lower")
      .forEach((btn) => btn.classList.remove("active"));
    updateUpperLowerButtons();
  } else if (visibilityMode != VISIBILITY_MODES.UPPER) {
    toggleVisibility(canvasId, VISIBILITY_MODES.UPPER);
  } else {
    toggleVisibility(canvasId, VISIBILITY_MODES.UPPER_LOWER);
  }
};

const toggleMandView = (canvasId) => {
  let { camera, activeMaxilOrMandView, visibilityMode } =
    canvasManager.get(canvasId);

  if (activeMaxilOrMandView != "mand") {
    activeMaxilOrMandView = "mand";
  } else {
    activeMaxilOrMandView = "";
  }
  canvasManager.update(canvasId, { activeMaxilOrMandView });

  if (activeMaxilOrMandView === "mand") {
    new TWEEN.Tween(camera.position)
      .to(!isPortrait ? { x: 0, y: 90, z: 35 } : { x: -90, y: 0, z: 35 }, 1000)
      .easing(TWEEN.Easing.Cubic.Out)
      .start();
  } else {
    new TWEEN.Tween(camera.position)
      .to({ x: 0, y: 0, z: 112 }, 1000)
      .easing(TWEEN.Easing.Cubic.Out)
      .start();
  }
  // When turning OFF mand, always set UPPER_LOWER and sync toolbar (don't use visibilityMode:
  // deactivateUpperLowerIfActive may have already set it to UPPER_LOWER before we run).
  if (activeMaxilOrMandView === "") {
    toggleVisibility(canvasId, VISIBILITY_MODES.UPPER_LOWER);
    document
      .querySelectorAll(".toolbar-item.upper_lower")
      .forEach((btn) => btn.classList.remove("active"));
    updateUpperLowerButtons();
  } else if (visibilityMode != VISIBILITY_MODES.LOWER) {
    toggleVisibility(canvasId, VISIBILITY_MODES.LOWER);
  } else {
    toggleVisibility(canvasId, VISIBILITY_MODES.UPPER_LOWER);
  }
};

const toggleMaxilAndMandView = async () => {
  const existing = document.getElementById("maxilandmand-canvas");
  if (existing) {
    // Turn off maxil view on main canvas and reset to normal view
    setMaxilView("canvas", false);
    // Clean up IPR visualization before removing canvas
    cleanupIPRVisualization(existing.id);
    canvasManager.remove(existing.id);
    existing.remove();
    window.dispatchEvent(new Event("resize"));
    return;
  }
  canvasManager.getAll().forEach((canvas) => {
    deactivateUpperLowerIfActive(canvas.id);
  });
  const newCanvas = document.createElement("canvas");
  newCanvas.id = "maxilandmand-canvas";
  document.querySelector(".renderer-container").appendChild(newCanvas);

  const mainCanvas = canvasManager.get("canvas");
  // Preserve main canvas hidden state (gums, attachments, etc.) so it is re-applied after setting maxil view
  const mainHiddenBefore = { ...mainCanvas.hiddenModels };
  console.log({isPortrait})
  if (!isPortrait) {
    mainCanvas.camera.zoom = DESKTOP_CAMERA_ZOOM;
  } else {
    mainCanvas.camera.zoom = 0.3;
  }
  mainCanvas.camera.updateProjectionMatrix();

  await initializeCanvas(newCanvas.id);

  // Apply main canvas hidden state to new canvas so both respect gums/attachments etc.
  canvasManager.update(newCanvas.id, { hiddenModels: mainHiddenBefore });

  // Use setMaxilView and setMandView to force the correct state
  // Force camera update to ensure correct position regardless of previous state
  // This ensures maxil is ON for main canvas and mand is ON for new canvas
  setMaxilView(mainCanvas.id, true, true);
  setMandView(newCanvas.id, true, true);

  // Re-apply main canvas hidden state so gums etc. stay as user had them (avoids gums becoming visible when they were off)
  canvasManager.update("canvas", { hiddenModels: mainHiddenBefore });
  toggleVisibility("canvas", VISIBILITY_MODES.UPPER);
  // Explicitly hide gum on main canvas when user had gums off (defensive: ensure mesh.visible matches hiddenModels)
  const applyGumsHidden = () => {
    if (!mainHiddenBefore.gums) return;
    const { models } = canvasManager.get("canvas");
    if (!models?.UPPER) return;
    const { upperActiveNum: uIdx } = getActiveAlignerNumbers();
    const idx = uIdx || Object.keys(models.UPPER)[0] || "00";
    const upperGum = models.UPPER[idx]?.gum;
    if (upperGum) upperGum.visible = false;
  };
  applyGumsHidden();
  // Re-apply after next frame so we override any code that shows gums during/after resize or render
  requestAnimationFrame(() => {
    applyGumsHidden();
  });

  // Initialize IPR visualization for the new canvas with current active aligner numbers
  // Wait for models to load and then initialize IPR
  setTimeout(() => {
    const { upperActiveNum, lowerActiveNum } = getActiveAlignerNumbers();
    initializeIPRVisualization(newCanvas.id, upperActiveNum, lowerActiveNum);
  }, 1000); // Increased delay to ensure models are loaded
};

const deactivateUpperLowerIfActive = (canvasId) => {
  const { visibilityMode } = canvasManager.get(canvasId);
  if (
    visibilityMode === VISIBILITY_MODES.UPPER ||
    visibilityMode === VISIBILITY_MODES.LOWER
  ) {
    toggleVisibility(canvasId, VISIBILITY_MODES.UPPER_LOWER);
    // Sync toolbar: clear active state on upper/lower buttons
    const btns = document.querySelectorAll(".toolbar-item.upper_lower");
    btns.forEach((btn) => btn.classList.remove("active"));
    updateUpperLowerButtons();
  }
};

const toggleGrid = () => {
  sharedData.isGridVisible = !sharedData.isGridVisible;
  canvasManager.getAll().forEach((canvas) => {
    canvas.overlayScene.visible = sharedData.isGridVisible;
  });
};

/**
 * Check if maxil or mand view is active across all canvases
 * @returns {boolean} True if either maxil or mand is active
 */
const isMaxilOrMandActive = () => {
  let hasMaxil = false;
  let hasMand = false;
  
  canvasManager.getAll().forEach((canvas) => {
    if (canvas.activeMaxilOrMandView === "maxil") {
      hasMaxil = true;
    }
    if (canvas.activeMaxilOrMandView === "mand") {
      hasMand = true;
    }
  });
  
  return hasMaxil || hasMand;
};

/**
 * Update upper and lower button states based on maxil/mand active status
 * Disables upper/lower when either maxil or mand is active
 */
const updateUpperLowerButtons = () => {
  const maxilOrMandActive = isMaxilOrMandActive();
  const upperBtn = document.getElementById("toolbar-item-upper");
  const lowerBtn = document.getElementById("toolbar-item-lower");

  if (upperBtn) {
    upperBtn.disabled = maxilOrMandActive;
  }
  if (lowerBtn) {
    lowerBtn.disabled = maxilOrMandActive;
  }
};

function resizeCanvas(
  canvas,
  camera,
  renderer,
  overlayCamera,
  gridMaterial,
  centerMaterial
) {
  const canvasCount = document.querySelectorAll(
    ".renderer-container > canvas"
  ).length;
  canvas.style.width = `calc(100% / ${canvasCount})`;
  canvas.style.height = "100%";
  canvas.width = canvas.offsetWidth;
  canvas.height = canvas.offsetHeight;

  if (isPortrait) {
    canvas.style.width = `calc(100% / ${canvasCount})`;
    canvas.style.height = `100%`;
    // canvas.style.flex = 1;
    canvas.width = canvas.offsetHeight;
    canvas.height = canvas.offsetWidth;
  } else {
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
  }

  const [canvas1, canvas2] = document.querySelectorAll(
    ".renderer-container > canvas"
  );

  if (isPortrait) {
    if (canvasCount == 2) {
      canvas1.style.transform = "translate(-100%, -50%) rotate(90deg)";
      canvas2.style.transform = "translate(0%, -50%) rotate(90deg)";
    } else {
      canvas1.style.transform = "translate(-50%, -50%) rotate(90deg)";
    }
  }

  onWindowResize(
    canvas,
    camera,
    renderer,
    overlayCamera,
    gridMaterial,
    centerMaterial
  );
}

function onWindowResize(
  canvas,
  camera,
  renderer,
  overlayCamera,
  gridMaterial,
  centerMaterial
) {
  const { width, height } = canvas;
  const screenDimensions = {
    width: canvas.width,
    height: canvas.height,
  };
  // const aspectRatio = Math.round((width / height) * 10);
  screenDimensions.width = width;
  screenDimensions.height = height;
  camera.aspect = width / height;

  if (isPortrait) {
    camera.left = -width / 7.6;
    camera.right = width / 7.6;
    camera.top = height / 7.6;
    camera.bottom = -height / 7.6;
  } else {
    camera.left = -width / 12.5;
    camera.right = width / 12.5;
    camera.top = height / 12.5;
    camera.bottom = -height / 12.5;
  }
  camera.updateProjectionMatrix();
  renderer.setSize(width, height);

  const aspect = width / height;
  overlayCamera.left = -1 * aspect;
  overlayCamera.right = 1 * aspect;
  overlayCamera.top = 1;
  overlayCamera.bottom = -1;
  overlayCamera.updateProjectionMatrix();
  gridMaterial.resolution.set(width, height);
  centerMaterial.resolution.set(width, height);
}

const changeCameraAngle = (camera, type) => {
  switch (type) {
    case "front":
      new TWEEN.Tween(camera.position)
        .to({ x: 0, y: 0, z: 112 }, 500)
        .easing(TWEEN.Easing.Cubic.Out)
        .start();
      break;
    case "rear":
      new TWEEN.Tween(camera.position)
        .to({ x: 0, y: 0, z: -112 }, 1500)
        .easing(TWEEN.Easing.Cubic.Out)
        .start();
      break;
    case "left":
      new TWEEN.Tween(camera.position)
        .to(
          !isPortrait ? { x: 112, y: 0, z: 20 } : { x: 0, y: 112, z: 20 },
          500
        )
        .easing(TWEEN.Easing.Cubic.Out)
        .start();
      break;
    case "right":
      new TWEEN.Tween(camera.position)
        .to(
          !isPortrait ? { x: -112, y: 0, z: 20 } : { x: 0, y: -112, z: 20 },
          500
        )
        .easing(TWEEN.Easing.Cubic.Out)
        .start();
      break;
    case "top":
      new TWEEN.Tween(camera.position)
        .to(
          !isPortrait ? { x: 0, y: 112, z: 20 } : { x: 112, y: 0, z: 20 },
          500
        )
        .easing(TWEEN.Easing.Cubic.Out)
        .start();
      break;
    case "bottom":
      new TWEEN.Tween(camera.position)
        .to(
          !isPortrait ? { x: 0, y: -112, z: 20 } : { x: -112, y: 0, z: 20 },
          500
        )
        .easing(TWEEN.Easing.Cubic.Out)
        .start();
      break;
    default:
      break;
  }
};

async function downloadFile(id) {
  const { data } = await axios.get(`${process.env.API_PREFIX}/report?id=${id}`);
  if (data.pdfUrl) {
    const link = document.createElement("a");
    link.download = data.fileName;

    link.href = data.pdfUrl;

    link.click();
  }
}

function bindEventListeners() {
  // const visibilityTypes = ["upper_lower", "upper", "lower"];
  // visibilityTypes.forEach((type) => {
  //   const control = document.getElementById(`show-${type}`);
  //   control.addEventListener("click", (e) => {
  //     toggleVisibility(type);
  //   });
  // });

  // const cameraTypes = ["front", "rear", "left", "right", "top", "bottom"];
  // cameraTypes.forEach((type) => {
  //   const control = document.getElementById(`camera-${type}`);
  //   control.addEventListener("click", (e) => {
  //     changeCameraAngle(type);
  //   });
  // });

  // ["lower", "upper"].forEach((type) => {
  //   ["prev", "next"].forEach((controlType) => {
  //     const centerControl = document.querySelector(
  //       `#${type}-control > .control-${controlType}`
  //     );
  //     centerControl.addEventListener("click", (e) => {
  //       const newIdx = changeActiveIndex(type, controlType);
  //       if (!newIdx) return;
  //       document.querySelector(
  //         `#${type}-control > #${type}-active-index`
  //       ).textContent = newIdx;
  //     });
  //   });
  // });

  document.getElementById("play").addEventListener("click", () => {
    play("canvas");
  });
  document.getElementById("play-next").addEventListener("click", () => {
    const slider = document.getElementById("player-slider");
    const newValue = Number(slider.value) + 1;
    // canvasManager.getAll().forEach((canvas) => {
    //   changeActiveIndexByValue(canvas.id, newValue);
    // });

    slider.value = newValue;
  });
  document.getElementById("play-prev").addEventListener("click", () => {
    const slider = document.getElementById("player-slider");
    const newValue = Number(slider.value) - 1;
    // canvasManager.getAll().forEach((canvas) => {
    //   changeActiveIndexByValue(canvas.id, newValue);
    // });
    slider.value = newValue;
  });

  // --- Toolbar action queue ---
  // Serialises toolbar actions so only one runs at a time. All toolbar buttons
  // are disabled while an action is in progress, preventing rapid clicks from
  // causing model visibility to desync with toolbar state.
  const TOOLBAR_ACTION_TYPES = [
    "superImposition",
    "attachments",
    "gums",
    "upper",
    "lower",
    "front",
    "left",
    "rear",
    "right",
    "diff-view",
    "maxil",
    "mand",
    "maxil_mand",
    "grid",
    "pdf-files",
    "movement-table",
    "ipr",
  ];

  let toolbarActionQueue = Promise.resolve();

  const disableAllToolbarButtons = () => {
    TOOLBAR_ACTION_TYPES.forEach((t) => {
      const btn = document.getElementById(`toolbar-item-${t}`);
      if (btn) btn.disabled = true;
    });
  };

  const TOOLBAR_DISABLE_RULES = {
    maxil_mand: [
      "front", "left", "rear", "right",
      "upper", "lower", "maxil", "mand", "diff-view",
    ],
    "diff-view": ["maxil_mand"],
  };

  const restoreToolbarButtonStates = () => {
    TOOLBAR_ACTION_TYPES.forEach((t) => {
      const btn = document.getElementById(`toolbar-item-${t}`);
      if (btn) btn.disabled = false;
    });

    // Re-apply disable rules based on currently active toggle buttons
    Object.entries(TOOLBAR_DISABLE_RULES).forEach(([key, targets]) => {
      const activeBtn = document.getElementById(`toolbar-item-${key}`);
      if (activeBtn && activeBtn.classList.contains("active")) {
        targets.forEach((target) => {
          const targetBtn = document.getElementById(`toolbar-item-${target}`);
          if (targetBtn) targetBtn.disabled = true;
        });
      }
    });

    // Re-apply maxil/mand → upper/lower disable rule
    updateUpperLowerButtons();
  };

  const enqueueToolbarAction = (actionFn) => {
    toolbarActionQueue = toolbarActionQueue.then(async () => {
      disableAllToolbarButtons();
      try {
        await actionFn();
      } catch (err) {
        console.error("Toolbar action failed:", err);
      } finally {
        restoreToolbarButtonStates();
      }
    });
  };

  TOOLBAR_ACTION_TYPES.forEach((type) => {
    const handleAction = async () => {
      if (type === "report") {
        const urlParams = new URLSearchParams(window.location.search);
        const id = urlParams.get("id");
        await downloadFile(id);
        return;
      } else if (["upper", "lower"].includes(type)) {
        canvasManager.getAll().forEach((canvas) => {
          const { visibilityMode } = canvas;

          if (
            (visibilityMode === VISIBILITY_MODES.UPPER &&
              type === VISIBILITY_MODES.UPPER) ||
            (visibilityMode === VISIBILITY_MODES.LOWER &&
              type === VISIBILITY_MODES.LOWER)
          ) {
            toggleVisibility(canvas.id, VISIBILITY_MODES.UPPER_LOWER);
          } else toggleVisibility(canvas.id, type);

          // canvasManager.update(canvas.id, {
          //   visibilityMode: type,
          // });
        });

        return;
      } else if (["front", "left", "rear", "right"].includes(type)) {
        canvasManager.getAll().forEach((canvas) => {
          changeCameraAngle(canvas.camera, type);
          // Update IPR visibility after camera angle changes
          setTimeout(() => {
            updateIPRVisibility(canvas.id);
          }, 550); // Wait for camera animation to complete
        });
        return;
      } else if (type === "diff-view") {
        await toggleDiffView();
        return;
      } else if (type === "maxil") {
        const allCanvases = canvasManager.getAll();
        const wasMaxilOrMandOnBefore = allCanvases.some(
          (c) =>
            c.activeMaxilOrMandView === "maxil" ||
            c.activeMaxilOrMandView === "mand"
        );
        allCanvases.forEach((canvas) => {
          deactivateUpperLowerIfActive(canvas.id);
          toggleMaxilView(canvas.id);
        });
        // Keep diff-canvas visibility in sync with main canvas after toggling maxil
        const diffData = canvasManager.get("diff-canvas");
        if (diffData) {
          const mainCanvas = canvasManager.get("canvas");
          canvasManager.update("diff-canvas", {
            visibilityMode: mainCanvas.visibilityMode,
            hiddenModels: { ...mainCanvas.hiddenModels },
          });
          toggleVisibility("diff-canvas", mainCanvas.visibilityMode);
          // When we just turned off maxil/mand with diff view active, ensure both cameras go to default front position
          if (wasMaxilOrMandOnBefore) {
            changeCameraAngle(mainCanvas.camera, "front");
            changeCameraAngle(diffData.camera, "front");
          }
        }
        return;
      } else if (type === "mand") {
        const allCanvases = canvasManager.getAll();
        const wasMaxilOrMandOnBefore = allCanvases.some(
          (c) =>
            c.activeMaxilOrMandView === "maxil" ||
            c.activeMaxilOrMandView === "mand"
        );
        allCanvases.forEach((canvas) => {
          deactivateUpperLowerIfActive(canvas.id);
          toggleMandView(canvas.id);
        });
        // Keep diff-canvas visibility in sync with main canvas after toggling mand
        const diffData = canvasManager.get("diff-canvas");
        if (diffData) {
          const mainCanvas = canvasManager.get("canvas");
          canvasManager.update("diff-canvas", {
            visibilityMode: mainCanvas.visibilityMode,
            hiddenModels: { ...mainCanvas.hiddenModels },
          });
          toggleVisibility("diff-canvas", mainCanvas.visibilityMode);
          // When we just turned off maxil/mand with diff view active, ensure both cameras go to default front position
          if (wasMaxilOrMandOnBefore) {
            changeCameraAngle(mainCanvas.camera, "front");
            changeCameraAngle(diffData.camera, "front");
          }
        }
        return;
      } else if (type === "maxil_mand") {
        await toggleMaxilAndMandView();
        return;
      } else if (type === "grid") {
        toggleGrid();
        return;
      } else if (type === "pdf-files") {
        MicroModal.show("pdf-files-modal");
        return;
      } else if (type === "movement-table") {
        toggleMovementPanel();
        return;
      } else if (type === "ipr") {
        // Toggle IPR once globally; toggleIPR updates all canvases
        const canvases = canvasManager.getAll();
        if (canvases.length) toggleIPR(canvases[0].id);
        return;
      }
      const canvases = canvasManager.getAll();
      const syncTypes = ["gums", "attachments", "superImposition"];
      if (syncTypes.includes(type) && canvases.length > 0) {
        const newHidden = !canvasManager.get(canvases[0].id).hiddenModels[type];
        canvases.forEach((canvas) =>
          setModelVisibility(canvas.id, type, newHidden)
        );
      } else {
        canvases.forEach((canvas) => {
          toggleModelVisibility(canvas.id, type);
        });
      }
    };

    document
      .getElementById(`toolbar-item-${type}`)
      .addEventListener("click", () => enqueueToolbarAction(handleAction));
  });
}

// Store last update time for throttling IPR visibility updates
let lastIPRUpdate = 0;

const render = (
  renderer,
  camera,
  controls,
  scene,
  mainLight,
  overlayCamera,
  overlayScene,
  canvasId
) => {
  requestAnimationFrame(() =>
    render(
      renderer,
      camera,
      controls,
      scene,
      mainLight,
      overlayCamera,
      overlayScene,
      canvasId
    )
  );
  renderer.render(scene, camera);
  renderer.autoClear = false;
  TWEEN.update();
  controls.update();

  // Update IPR visibility when camera moves (throttled to every 100ms)
  if (canvasId && Date.now() - lastIPRUpdate > 100) {
    updateIPRVisibility(canvasId);
    lastIPRUpdate = Date.now();
  }

  // Update label positions every frame for visible labels
  if (canvasId && window.iprLines) {
    const lines = window.iprLines.get(canvasId) || [];
    lines.forEach(({ label, endPoint, line }) => {
      if (!label || !line || !line.visible) return;
      if (isMobilePortrait()) {
        positionMobileIPRLabelFromLineEnd(label, line, camera, renderer, endPoint);
      } else if (endPoint) {
        updateLabelPosition(label, endPoint, camera, renderer);
      }
    });
  }

  mainLight.position.set(
    camera.position.x + camera.position.x * 1.1,
    camera.position.y + camera.position.y * 0.6,
    camera.position.z - camera.position.z * 0.1
  );
  renderer.render(overlayScene, overlayCamera);
  renderer.autoClear = true;
};

function setupToothClickHighlight() {
  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2();
  let mouseDownX = 0;
  let mouseDownY = 0;
  let touchStartX = 0;
  let touchStartY = 0;

  const rendererContainer = document.querySelector('.renderer-container');

  const handleToothPick = (clientX, clientY, canvasEl) => {
    const canvasData = canvasManager.get(canvasEl.id);
    if (!canvasData) return;

    const { camera, scene } = canvasData;
    const rect = canvasEl.getBoundingClientRect();
    mouse.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((clientY - rect.top) / rect.height) * 2 + 1;

    raycaster.setFromCamera(mouse, camera);

    const toothMeshes = scene.children.filter(
      (obj) => obj.userData.type === 'tooth' && obj.material?.visible
    );
    const intersects = raycaster.intersectObjects(toothMeshes, false);

    if (intersects.length === 0) {
      deselectTooth();
      return;
    }

    const { toothId } = intersects[0].object.userData;
    if (!toothId) return;
    selectTooth(toothId.jaw, toothId.toothIdx);
  };

  rendererContainer.addEventListener('mousedown', (e) => {
    mouseDownX = e.clientX;
    mouseDownY = e.clientY;
  });

  rendererContainer.addEventListener('click', (e) => {
    if (Math.abs(e.clientX - mouseDownX) > 5 || Math.abs(e.clientY - mouseDownY) > 5) return;
    const target = e.target;
    if (!(target instanceof HTMLCanvasElement)) return;
    handleToothPick(e.clientX, e.clientY, target);
  });

  rendererContainer.addEventListener('touchstart', (e) => {
    if (e.touches.length === 1) {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    }
  }, { passive: true });

  rendererContainer.addEventListener('touchend', (e) => {
    if (e.changedTouches.length !== 1) return;
    const touch = e.changedTouches[0];
    if (Math.abs(touch.clientX - touchStartX) > 10 || Math.abs(touch.clientY - touchStartY) > 10) return;
    const target = document.elementFromPoint(touch.clientX, touch.clientY);
    if (!(target instanceof HTMLCanvasElement)) return;
    handleToothPick(touch.clientX, touch.clientY, target);
  });
}

function createToolbar(items) {
  const toolbar = document.getElementById("toolbar");
  const toolbarItems = document.createElement("div");
  toolbarItems.className = "toolbar-items";

  const disableRules = {

    maxil_mand: [
      "front",
      "left",
      "rear",
      "right",
      "upper",
      "lower",
      "maxil",
      "mand",
      "diff-view",
    ],
    "diff-view": ["maxil_mand"],
   
  };

  items.forEach((item) => {
    const button = document.createElement("button");
    button.className = "toolbar-item";
    if (item.defaultActive) {
      button.className += " active";
    }
    button.id = "toolbar-item-" + (item.type || item.text.toLowerCase());

    const icon = document.createElement("div");
    icon.className = `iconfont icon-${item.icon}`;

    const label = document.createElement("span");
    label.className = "label";
    label.textContent = item.text;

    button.appendChild(icon);
    button.appendChild(label);
    toolbarItems.appendChild(button);

    if (item.canToggle) {
      button.addEventListener("click", () => {
        if (item.toggleGroup) {
          document
            .querySelectorAll(`.toolbar-item.${item.toggleGroup}`)
            .forEach((btn) => {
              if (btn !== button) {
                btn.classList.remove("active");
              }
            });
        }
        button.classList.toggle("active");

        Object.entries(disableRules).forEach(([key, targets]) => {
          if (item.type === key) {
            targets.forEach((target) => {
              const targetBtn = document.getElementById(
                `toolbar-item-${target}`
              );
              if (targetBtn) {
                targetBtn.disabled = button.classList.contains("active");
              }
            });
          }
        });

        Object.entries(disableRules).forEach(([key, targets]) => {
          const activeBtn = document.getElementById(`toolbar-item-${key}`);
          if (!activeBtn || !activeBtn.classList.contains("active")) {
            targets.forEach((target) => {
              const targetBtn = document.getElementById(
                `toolbar-item-${target}`
              );
              if (targetBtn) {
                targetBtn.disabled = false;
              }
            });
          }
        });

        // Update upper/lower buttons based on maxil/mand state
        // This needs to happen after a delay to allow canvas state to update
        if (item.type === "maxil" || item.type === "mand") {
          setTimeout(() => {
            updateUpperLowerButtons();
          }, 100);
        }
      });
      if (item.toggleGroup) {
        button.classList.add(item.toggleGroup);
      }
    }
  });

  toolbar.appendChild(toolbarItems);
}

const toolbarData = [
  {
    icon: "attachments",
    text: "Attach",
    type: "attachments",
    canToggle: true,
    defaultActive: true,
  },
  { icon: "overlap", text: "Super", type: "superImposition", canToggle: true },
  { icon: "occlusal", text: "Gums", type: "gums", canToggle: true, defaultActive: true },
  { icon: "deglazing", text: "IPR", type: "ipr", canToggle: true, defaultActive: true },
  {
    icon: "reveal_upper",
    text: "Upper",
    type: "upper",
    canToggle: true,
    toggleGroup: "upper_lower",
  },
  {
    icon: "reveal_lower",
    text: "Lower",
    type: "lower",
    canToggle: true,
    toggleGroup: "upper_lower",
  },
  { icon: "zhengmian", text: "Front", type: "front" },
  { icon: "left_side", text: "Left", type: "left" },
  { icon: "back_vision", text: "Back", type: "rear" },
  { icon: "right_side", text: "Right", type: "right" },
  {
    icon: "upper_teeth",
    text: "Maxil",
    type: "maxil",
    canToggle: true,
    toggleGroup: "maxil_mand",
  },
  {
    icon: "lower_teeth",
    text: "Mand",
    type: "mand",
    canToggle: true,
    toggleGroup: "maxil_mand",
  },
  {
    icon: "hemian",
    text: "View",
    canToggle: true,
    type: "maxil_mand",
    toggleGroup: "maxil_mand",
  },
  {
    icon: "contrast",
    text: "Differ",
    type: "diff-view",
    canToggle: true,
  },
  {
    icon: "grid",
    text: "Grid",
    type: "grid",
    canToggle: true,
  },
  {
    icon: "data",
    text: "Files",
    type: "pdf-files",
  },
  {
    icon: "movement",
    text: "Movement Table",
    type: "movement-table",
  },
];

createToolbar(toolbarData);

initializeCanvas("canvas");

bindEventListeners();
setupToothClickHighlight();

try {
  initMovementPanel();
} catch (err) {
  console.error("initMovementPanel failed", err);
}

// Expose tooth mapping and IPR functions for debugging
window.getToothMapping = getToothMapping;
window.findIPRLines = findIPRLines;
window.testFDIMapping = testFDIMapping;
window.canvasManager = canvasManager;

const playerWrapper = document.getElementById("player-wrapper");
const $slider = document.createElement("tc-range-slider");
$slider.setAttribute("id", "player-slider");
$slider.setAttribute("min", "0");

$slider.setAttribute(
  "style",
  "--opacity: 0.95; margin-left: auto; margin-right: auto"
);
if (isPortrait) {
  $slider.setAttribute("type", "vertical");
}
$slider.setAttribute("value1", "0");
$slider.setAttribute("generate-labels", "false");
$slider.setAttribute("moving-tooltip", "true");
$slider.setAttribute("moving-tooltip-width", "30");
$slider.setAttribute("moving-tooltip-height", "36");
$slider.setAttribute(
  "moving-tooltip-bg",
  `transparent url(${dropStep}) no-repeat center/contain`
);

$slider.setAttribute("step", "1");
$slider.setAttribute("round", "0");
$slider.setAttribute("pointer-width", "50px");
$slider.setAttribute("pointer-height", "50px");
$slider.setAttribute("moving-tooltip-distance-to-pointer", "20");
$slider.setAttribute("pointer-bg", "transparent");
$slider.setAttribute("pointer-bg-focus", "transparent");
$slider.setAttribute("pointer-bg-hover", "transparent");
$slider.setAttribute("pointer-border", "none");
$slider.setAttribute("pointer-border-hover", "none");
$slider.setAttribute("pointer-border-focus", "none");
$slider.setAttribute("pointer-shadow", "none");
$slider.setAttribute("pointer-shadow-hover", "none");
$slider.setAttribute("pointer-shadow-focus", "none");

playerWrapper.append($slider);

$slider.addEventListener("change", (evt) => {
  canvasManager.getAll().forEach((canvas) => {
    changeActiveIndexByValue(canvas.id, evt.detail.value);
  });
});

// ── Export Modal Logic ────────────────────────────────────────────────────────

(function initExportModal() {
  // Speed definitions: ms per aligner frame
  const SPEED_MAP = { slow: 400, middle: 220, fast: 100 };

  let selectedFormat = "gif";
  let selectedSpeed = "slow";
  let isExporting = false;

  // Open modal via export button in app bar
  const exportBtn = document.getElementById("export-btn");
  if (exportBtn) {
    exportBtn.addEventListener("click", () => {
      MicroModal.show("export-modal");
    });
  }

  // Option group toggle (format)
  document.getElementById("export-format-group").addEventListener("click", (e) => {
    const btn = e.target.closest(".export-option");
    if (!btn) return;
    document.querySelectorAll("#export-format-group .export-option").forEach((b) => b.classList.remove("active"));
    btn.classList.add("active");
    selectedFormat = btn.dataset.value;
  });

  // Option group toggle (speed)
  document.getElementById("export-speed-group").addEventListener("click", (e) => {
    const btn = e.target.closest(".export-option");
    if (!btn) return;
    document.querySelectorAll("#export-speed-group .export-option").forEach((b) => b.classList.remove("active"));
    btn.classList.add("active");
    selectedSpeed = btn.dataset.value;
  });

  // Start export
  document.getElementById("export-start-btn").addEventListener("click", async () => {
    if (isExporting) return;
    await runExport(selectedFormat, selectedSpeed);
  });

  function setProgress(pct, label) {
    document.getElementById("export-progress-fill").style.width = pct + "%";
    document.getElementById("export-progress-label").textContent = label;
  }

  function setExportingUI(exporting) {
    isExporting = exporting;
    const progressArea = document.getElementById("export-progress-area");
    const startBtn = document.getElementById("export-start-btn");
    const cancelBtn = document.getElementById("export-cancel-btn");
    progressArea.style.display = exporting ? "block" : "none";
    startBtn.disabled = exporting;
    cancelBtn.disabled = exporting;
    if (!exporting) setProgress(0, "Preparing…");
  }

  // Composite IPR labels onto the export canvas.
  // Labels are HTML elements — canvas.toDataURL() can't see them, so we read
  // their actual DOM style and repaint them as canvas primitives, matching the
  // diamond shape, white fill, drop-shadow, and text exactly.
  function drawIPRLabelsOntoCanvasAt(ctx, threeRenderer, threeCamera, canvasId, dx, dy, dw, dh) {
    const lines = window.iprLines ? (window.iprLines.get(canvasId) || []) : [];
    const src = threeRenderer.domElement;
    const cssW = src.clientWidth || src.width;
    const cssH = src.clientHeight || src.height;
    // Pixels-per-CSS-pixel in the destination region (so labels match on-screen size)
    const scaleX = dw / cssW;
    const scaleY = dh / cssH;
    const scale = (scaleX + scaleY) / 2;

    lines.forEach(({ label, endPoint }) => {
      if (!label || !endPoint || label.dataset.visible !== "true") return;

      // --- position: project 3D end-point into CSS-pixel space, then translate to dest ---
      const vec = endPoint.clone().project(threeCamera);
      const xCss = (vec.x * 0.5 + 0.5) * cssW;
      const yCss = (vec.y * -0.5 + 0.5) * cssH;
      const cx = dx + xCss * scaleX;
      const cy = dy + yCss * scaleY;

      // --- read text from the DOM (innermost span holds the value) ---
      const innerSpans = label.querySelectorAll("span");
      let text = "";
      innerSpans.forEach((s) => { if (!text && s.textContent.trim()) text = s.textContent.trim(); });
      if (!text) text = label.textContent.trim();
      if (!text) return;

      // --- size: read from computed style of the outer label ---
      const computedSize = parseFloat(getComputedStyle(label).width) || 26;
      const half = (computedSize / 2) * scale;

      // --- shadow ---
      ctx.save();
      ctx.shadowColor = "rgba(0,0,0,0.22)";
      ctx.shadowBlur = 4 * scale;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 1 * scale;

      // --- diamond fill: read highlight state from the live DOM span ---
      const diamondEl = label.querySelector("span");
      const isHighlighted = diamondEl &&
        (diamondEl.style.background || "").includes("gradient");

      ctx.translate(cx, cy);
      ctx.rotate(Math.PI / 4);

      if (isHighlighted) {
        // Match linear-gradient(135deg, #8CCEE6, #6FC3DF)
        // 135° in canvas space = top-left → bottom-right within the rotated square
        const grad = ctx.createLinearGradient(-half, -half, half, half);
        grad.addColorStop(0, "#8CCEE6");
        grad.addColorStop(1, "#6FC3DF");
        ctx.fillStyle = grad;
      } else {
        ctx.fillStyle = "#ffffff";
      }

      ctx.beginPath();
      ctx.rect(-half, -half, half * 2, half * 2);
      ctx.fill();
      ctx.restore();

      // --- text (drawn upright, centred on the same point) ---
      ctx.save();
      const fontSize = Math.round((computedSize * 0.38) * scale);
      ctx.font = `normal ${fontSize}px sans-serif`;
      ctx.fillStyle = "#000000";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(text, cx, cy);
      ctx.restore();
    });
  }

  // Composite every active canvas (main + diff-canvas + maxilandmand-canvas)
  // into a single output frame, laid out exactly as they appear on screen.
  function captureCompositeFrame() {
    const canvases = canvasManager.getAll();
    const container = document.querySelector(".renderer-container");
    const containerRect = container.getBoundingClientRect();

    // Force a fresh render on every active canvas so preserveDrawingBuffer holds the latest frame
    canvases.forEach(({ renderer, scene, camera, overlayScene, overlayCamera }) => {
      renderer.render(scene, camera);
      renderer.autoClear = false;
      renderer.render(overlayScene, overlayCamera);
      renderer.autoClear = true;
    });

    // Pick output resolution from the main canvas's DPR
    const main = canvasManager.get("canvas") || canvases[0];
    const mainSrc = main.renderer.domElement;
    const mainRect = main.canvas.getBoundingClientRect();
    const dpr = mainSrc.width / (mainRect.width || mainSrc.width);

    const flat = document.createElement("canvas");
    flat.width = Math.max(1, Math.round(containerRect.width * dpr));
    flat.height = Math.max(1, Math.round(containerRect.height * dpr));
    const ctx = flat.getContext("2d");
    ctx.fillStyle = "#f0f2f5";
    ctx.fillRect(0, 0, flat.width, flat.height);

    canvases.forEach((c) => {
      const src = c.renderer.domElement;
      const rect = c.canvas.getBoundingClientRect();
      const dx = Math.round((rect.left - containerRect.left) * dpr);
      const dy = Math.round((rect.top - containerRect.top) * dpr);
      const dw = Math.round(rect.width * dpr);
      const dh = Math.round(rect.height * dpr);
      if (dw <= 0 || dh <= 0) return;

      ctx.drawImage(src, 0, 0, src.width, src.height, dx, dy, dw, dh);
      drawIPRLabelsOntoCanvasAt(ctx, c.renderer, c.camera, c.id, dx, dy, dw, dh);
    });

    return flat.toDataURL("image/png");
  }

  async function captureFrames() {
    const canvases = canvasManager.getAll();
    if (!canvases.length || !canvases.every((c) => c.camera && c.renderer && c.scene)) {
      throw new Error(
        "Canvas is not initialized yet. Please wait for the model to finish loading and try again."
      );
    }
    // Use the authoritative model count rather than the slider attribute
    const maxAligner = getMaxAlignerNumber("canvas");
    const frames = [];
    for (let i = 0; i <= maxAligner; i++) {
      canvases.forEach((c) => changeActiveIndexByValue(c.id, i));
      // Update IPR line/label visibility on every active canvas so labels composite correctly
      canvases.forEach((c) => updateIPRVisibility(c.id));
      // Wait two rAFs: one for model visibility to propagate, one for Three.js render loop
      await new Promise((r) => requestAnimationFrame(r));
      await new Promise((r) => requestAnimationFrame(r));
      frames.push(captureCompositeFrame());
      setProgress(Math.round((i / maxAligner) * 65), `Capturing frame ${i + 1} / ${maxAligner + 1}…`);
    }
    return frames;
  }

  // ── GIF export (uses bundled gif.js) ─────────────────────────────────────
  async function exportGIF(frames, frameDelayMs) {
    const firstImg = await loadImage(frames[0]);

    const gif = new GIF({
      workers: 2,
      quality: 10,
      width: firstImg.naturalWidth,
      height: firstImg.naturalHeight,
      workerScript: gifWorkerUrl,
    });

    setProgress(65, "Building GIF frames…");

    for (let i = 0; i < frames.length; i++) {
      const img = await loadImage(frames[i]);
      // Draw onto a canvas so gif.js gets pixel data (not just an img element)
      const fc = document.createElement("canvas");
      fc.width = img.naturalWidth;
      fc.height = img.naturalHeight;
      fc.getContext("2d").drawImage(img, 0, 0);
      gif.addFrame(fc, { delay: frameDelayMs, copy: true });
      setProgress(65 + Math.round((i / frames.length) * 20), `Building GIF frame ${i + 1} / ${frames.length}…`);
    }

    return new Promise((resolve, reject) => {
      gif.on("progress", (p) => setProgress(85 + Math.round(p * 14), "Encoding GIF…"));
      gif.on("finished", (blob) => resolve(blob));
      gif.on("error", reject);
      gif.render();
    });
  }

  // Helper: load an image, with onerror so a bad frame rejects rather than hangs forever.
  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Failed to load frame image"));
      img.src = src;
    });
  }

  // Pick the best MediaRecorder mimeType for this browser.
  // Returns { mimeType, container } where container is "mp4" if we can record
  // MP4 directly (Safari 14.1+) or "webm" if we need to transcode.
  function pickRecorderMime() {
    if (typeof MediaRecorder === "undefined") return null;
    const mp4Candidates = [
      "video/mp4;codecs=avc1.42E01E",
      "video/mp4;codecs=avc1",
      "video/mp4",
    ];
    for (const m of mp4Candidates) {
      if (MediaRecorder.isTypeSupported(m)) return { mimeType: m, container: "mp4" };
    }
    const webmCandidates = [
      "video/webm;codecs=vp9",
      "video/webm;codecs=vp8",
      "video/webm",
    ];
    for (const m of webmCandidates) {
      if (MediaRecorder.isTypeSupported(m)) return { mimeType: m, container: "webm" };
    }
    return null;
  }

  // ── MP4 export: frames → MediaRecorder. On browsers that record MP4 natively
  // (Safari 14.1+) we download the recording directly. Elsewhere we record WebM
  // and transcode to MP4 via ffmpeg.wasm. ────────────────────────────────────
  async function exportMP4(frames, frameDelayMs) {
    if (typeof MediaRecorder === "undefined") {
      throw new Error("Video export is not supported in this browser. Please try GIF instead.");
    }

    const firstImg = await loadImage(frames[0]);

    const offscreen = document.createElement("canvas");
    offscreen.width = firstImg.naturalWidth;
    offscreen.height = firstImg.naturalHeight;
    const ctx = offscreen.getContext("2d");

    if (typeof offscreen.captureStream !== "function") {
      throw new Error("Video export is not supported in this browser (canvas.captureStream missing). Please try GIF instead.");
    }

    const mime = pickRecorderMime();
    if (!mime) {
      throw new Error("This browser does not support video recording. Please try GIF instead.");
    }

    const stream = offscreen.captureStream();
    const recorder = new MediaRecorder(stream, { mimeType: mime.mimeType });
    const chunks = [];
    recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };

    // Attach onstop BEFORE calling stop() so we can't miss the event.
    const stopped = new Promise((resolve) => { recorder.onstop = resolve; });

    recorder.start();

    for (let i = 0; i < frames.length; i++) {
      const img = await loadImage(frames[i]);
      ctx.fillStyle = "#f0f2f5";
      ctx.fillRect(0, 0, offscreen.width, offscreen.height);
      ctx.drawImage(img, 0, 0);
      setProgress(65 + Math.round((i / frames.length) * 15), `Recording frame ${i + 1} / ${frames.length}…`);
      await new Promise((r) => setTimeout(r, frameDelayMs));
    }

    recorder.stop();
    await stopped;
    const recordedBlob = new Blob(chunks, { type: mime.mimeType });

    // If the recorder gave us MP4 directly, we're done — skip ffmpeg entirely.
    if (mime.container === "mp4") {
      setProgress(99, "Finalising…");
      return new Blob([await recordedBlob.arrayBuffer()], { type: "video/mp4" });
    }

    // Otherwise transcode WebM → MP4 via our self-contained ffmpeg classic worker.
    setProgress(81, "Loading transcoder…");

    // Fetch the UMD core JS text and WASM binary — turned into blob URLs so
    // importScripts() inside the classic worker sees a same-origin URL.
    const [coreResp, wasmResp] = await Promise.all([
      fetch(ffmpegCoreUrl),
      fetch(ffmpegWasmUrl),
    ]);
    if (!coreResp.ok) throw new Error(`Failed to load ffmpeg core (HTTP ${coreResp.status})`);
    if (!wasmResp.ok) throw new Error(`Failed to load ffmpeg wasm (HTTP ${wasmResp.status})`);
    const [coreText, wasmBuf] = await Promise.all([coreResp.text(), wasmResp.arrayBuffer()]);
    const coreBlobUrl = URL.createObjectURL(new Blob([coreText], { type: "text/javascript" }));

    const worker = new Worker(ffmpegWorkerUrl);

    // Load core inside the worker
    await new Promise((resolve, reject) => {
      worker.onmessage = (e) => {
        if (e.data.type === "ready") resolve();
        else if (e.data.type === "error") reject(new Error(e.data.message));
      };
      worker.onerror = (e) => reject(new Error(e.message || "ffmpeg worker error"));
      worker.postMessage({ type: "load", payload: { coreUrl: coreBlobUrl, wasmBinary: new Uint8Array(wasmBuf) } });
    });

    setProgress(85, "Transcoding to MP4…");

    // Feed the WebM blob as a Uint8Array and get back MP4 bytes
    const webmBytes = new Uint8Array(await recordedBlob.arrayBuffer());
    const mp4Bytes = await new Promise((resolve, reject) => {
      worker.onmessage = (e) => {
        if (e.data.type === "done") resolve(e.data.output);
        else if (e.data.type === "error") reject(new Error(e.data.message));
      };
      worker.onerror = (e) => reject(new Error(e.message || "ffmpeg worker error"));
      worker.postMessage(
        { type: "exec", payload: { args: ["-i", "input.webm", "-c:v", "libx264", "-preset", "fast", "-crf", "23", "-movflags", "+faststart", "-y", "output.mp4"], inputName: "input.webm", inputData: webmBytes } },
        [webmBytes.buffer]
      );
    });

    worker.terminate();
    URL.revokeObjectURL(coreBlobUrl);

    setProgress(99, "Finalising…");
    return new Blob([mp4Bytes.buffer], { type: "video/mp4" });
  }

  // Build "{patientName}-{designNo}" for the export download. Falls back to UID
  // when patientName is missing. The #planNo element renders as "Design No: X"
  // so we strip the prefix. Sanitises filesystem-unsafe characters.
  function getExportBaseName() {
    const patientName = document.getElementById("patientName")?.textContent?.trim();
    const uid =
      document.body.dataset.customerId ||
      document.getElementById("customerId")?.textContent?.trim() ||
      "";
    const planNoText = document.getElementById("planNo")?.textContent?.trim() || "";
    const designNo = planNoText.replace(/^design\s*no\s*:?\s*/i, "").trim();

    const namePart = patientName || uid || "dental-animation";
    const base = designNo ? `${namePart}-${designNo}` : namePart;
    return base.replace(/[\\/:*?"<>|]+/g, "_");
  }

  function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }

  async function runExport(format, speed) {
    const playerSlider = document.getElementById("player-slider");
    const frameDelayMs = SPEED_MAP[speed] || 340;
    const savedSliderValue = parseInt(playerSlider.value || "0", 10);

    setExportingUI(true);

    try {
      setProgress(0, "Capturing frames…");
      const frames = await captureFrames();

      setProgress(65, format === "gif" ? "Building GIF…" : "Encoding video…");
      let blob;
      if (format === "gif") {
        blob = await exportGIF(frames, frameDelayMs);
      } else {
        blob = await exportMP4(frames, frameDelayMs);
      }

      setProgress(100, "Done! Downloading…");
      const ext = format === "gif" ? "gif" : "mp4";
      downloadBlob(blob, `${getExportBaseName()}.${ext}`);

      // Restore slider to where user was
      canvasManager.getAll().forEach((c) => changeActiveIndexByValue(c.id, savedSliderValue));
      playerSlider.value = savedSliderValue;

      await new Promise((r) => setTimeout(r, 800));
      MicroModal.close("export-modal");
    } catch (err) {
      console.error("Export failed:", err);
      setProgress(0, "Export failed. Please try again.");
      await new Promise((r) => setTimeout(r, 2000));
    } finally {
      setExportingUI(false);
    }
  }
})();
